import {
	CaseDetailsLawyer,
	MultipleChoiceQuestion,
	Option,
	QuestionType,
} from '@/interfaces';
import * as React from 'react';
import { ClosedQuestion } from './ClosedQuestion';
import { LandingActionQuestion } from './LandingActionQuestion';
import {
	Control,
	FieldErrors,
	FieldValues,
	UseFieldArrayRemove,
	UseFieldArraySwap,
	UseFormClearErrors,
	UseFormGetValues,
	UseFormRegister,
	UseFormSetValue,
} from 'react-hook-form';
import { OpenQuestion } from './OpenQuestion';
import { DateQuestion } from './DateQuestion';
import clsx from 'clsx';
import { QuestionHeader } from './QuestionHeader';
import { MultipleAnswersQuestion } from './MultipleAnswersQuestion';
import { MultipleAnswerErrorI } from '@/components/screens/lawyerapp/cases/case-administration/applicant-intake-phase/LandingPageCaseClaimForm';

interface LandingQuestionI {
	index: number;
	questionType: QuestionType;
	totalQuestions: number;
	remove: UseFieldArrayRemove;
	swap: UseFieldArraySwap;
	register: UseFormRegister<FieldValues>;
	control: Control<FieldValues, any>;
	getValues: UseFormGetValues<FieldValues>;
	setValue: UseFormSetValue<FieldValues>;
	errors: FieldErrors<FieldValues>;
	isCaseReadOnly: boolean;
	clearErrors: UseFormClearErrors<FieldValues>;
	multipleChoiceQuestion: MultipleChoiceQuestion[] | undefined;
	multipleAnswersError: MultipleAnswerErrorI[];
	showMultipleAnswersError: boolean;
	caseDetails: CaseDetailsLawyer | undefined;
	classNameCard?: string;
	classNameQuestionInput?: string;
}

declare global {
	interface Window {
		__calendarRef?: React.RefObject<HTMLElement>;
	}
}

export const LandingQuestion: React.FC<LandingQuestionI> = ({
	index,
	questionType,
	totalQuestions,
	remove,
	swap,
	getValues,
	register,
	control,
	setValue,
	errors,
	isCaseReadOnly,
	caseDetails,
	clearErrors,
	multipleChoiceQuestion,
	multipleAnswersError,
	showMultipleAnswersError,
	classNameCard = '',
	classNameQuestionInput = '',
}) => {
	const [isActive, setIsActive] = React.useState(false);
	const formRef = React.useRef<HTMLDivElement | null>(null);
	const excepts = ['radix-:'];

	const handleFocus = () => {
		setIsActive(true);
	};

	// Handle clicks out of the form
	React.useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as HTMLElement;
			const calendarRef = window.__calendarRef?.current;

			const clickedOutsideForm =
				formRef.current && !formRef.current.contains(target);

			const clickedInsideCalendar = calendarRef && calendarRef.contains(target);

			const isNotException =
				!excepts.some((item) => target.id?.includes(item)) &&
				!clickedInsideCalendar;

			if (clickedOutsideForm && isNotException) {
				setIsActive(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const listCompanies = React.useMemo(() => {
		const listBrands: Option[] = [];

		if (caseDetails && caseDetails.brands) {
			caseDetails?.brands.forEach((brand) => {
				if (!brand.name || !brand.id) return;
				listBrands.push({
					label: brand.name,
					name: brand.id?.toString(),
				});
			});
		}
		return listBrands;
	}, [caseDetails]);

	return (
		<div
			className={clsx('bg-gray-20 p-4 rounded-2xl', classNameCard)}
			ref={formRef}
			onFocus={() => {
				if (questionType !== 'MULTIPLE') {
					handleFocus();
				}
			}}
			onClick={handleFocus}
		>
			<LandingActionQuestion
				remove={remove}
				swap={swap}
				index={index}
				totalQuestions={totalQuestions}
				questionType={questionType}
			/>

			<QuestionHeader
				index={index}
				isEditModeActived={isActive}
				register={register}
				getValues={getValues}
				errors={errors}
				classNameContainerInput={classNameQuestionInput}
			/>
			{questionType === 'YES_NO' ? (
				<ClosedQuestion
					index={index}
					getValues={getValues}
					register={register}
					setValue={setValue}
					errors={errors}
					disabled={isCaseReadOnly}
				/>
			) : questionType === 'TEXT' ? (
				<OpenQuestion index={index} register={register} />
			) : questionType === 'DATE' ? (
				<DateQuestion
					index={index}
					register={register}
					setValue={setValue}
					errors={errors}
					getValues={getValues}
				/>
			) : questionType === 'MULTIPLE' ? (
				<MultipleAnswersQuestion
					index={index}
					register={register}
					control={control}
					setValue={setValue}
					listCompanies={listCompanies}
					errors={errors}
					getValues={getValues}
					isEditModeActived={isActive}
					clearErrors={clearErrors}
					multipleChoiceQuestion={multipleChoiceQuestion}
					multipleAnswersError={multipleAnswersError}
					showMultipleAnswersError={showMultipleAnswersError}
				/>
			) : questionType === 'MULTIPLE' ? (
				<MultipleAnswersQuestion
					index={index}
					register={register}
					control={control}
					setValue={setValue}
					listCompanies={listCompanies}
					errors={errors}
					getValues={getValues}
					isEditModeActived={isActive}
					clearErrors={clearErrors}
					multipleChoiceQuestion={multipleChoiceQuestion}
					multipleAnswersError={multipleAnswersError}
					showMultipleAnswersError={showMultipleAnswersError}
				/>
			) : null}
		</div>
	);
};
